import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { ArrowLeft, RefreshCw, AlertTriangle, CheckCircle2, XCircle, HardDrive } from "lucide-react";

const StatusBadge = ({ status }) => {
  if (status === "EXISTS") return <span className="text-emerald-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> R2 OK</span>;
  if (status === "EXISTS_EMERGENT") return <span className="text-cyan-400 inline-flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Emergent OK</span>;
  if (status === "MISSING") return <span className="text-red-400 inline-flex items-center gap-1"><XCircle className="h-3 w-3" /> Mangler</span>;
  if (status === "NO_STORAGE_PATH") return <span className="text-amber-400 inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Ingen path</span>;
  if (status === "R2_NOT_CONFIGURED") return <span className="text-zinc-400">R2 av</span>;
  return <span className="text-red-300">{status}</span>;
};

/**
 * /admin/media-diagnose – bulk-sjekk av bilder mot R2/storage.
 * KUN owner. Viser hvilke bilder som mangler i R2, som ikke har storage_path,
 * eller som gir feil. Praktisk for opprydding og feilsøking uten DevTools.
 */
export default function AdminMediaDiagnose() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [onlyBroken, setOnlyBroken] = useState(true);
  const [limit, setLimit] = useState(100);
  const [offset, setOffset] = useState(0);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get(`/admin/media/diagnose`, {
        params: { limit, offset, only_broken: onlyBroken },
      });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Klarte ikke laste diagnose.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [onlyBroken, limit, offset]);

  const s = data?.summary || {};
  const items = data?.items || [];

  return (
    <Layout>
      <div className="max-w-6xl mx-auto">
        <Link to="/admin/board" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1 mb-3" data-testid="back-to-admin">
          <ArrowLeft className="h-3.5 w-3.5" /> Admin-oversikt
        </Link>

        <div className="flex items-center gap-3 mb-2">
          <HardDrive className="h-6 w-6 text-[var(--magenta)]" />
          <h1 className="font-fraunces italic text-4xl text-[var(--cream)]">Media-diagnose</h1>
        </div>
        <p className="text-[var(--cream-soft)] text-sm mb-6">
          Sjekker om hvert bildes fil finnes i R2-storage eller Emergent-storage (fallback). Nyeste først.
        </p>

        {/* Summary-kort */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <div className="panel-tight p-4 border border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-[var(--cream-soft)]">Bilder i DB</div>
            <div className="font-fraunces italic text-3xl text-[var(--cream)] mt-1">{s.total_in_db ?? "…"}</div>
          </div>
          <div className="panel-tight p-4 border border-emerald-500/30 bg-emerald-500/5">
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">R2 OK</div>
            <div className="font-fraunces italic text-3xl text-emerald-300 mt-1">{s.counts?.exists_r2 ?? 0}</div>
          </div>
          <div className="panel-tight p-4 border border-cyan-500/30 bg-cyan-500/5">
            <div className="text-[10px] uppercase tracking-widest text-cyan-400">Emergent OK</div>
            <div className="font-fraunces italic text-3xl text-cyan-300 mt-1">{s.counts?.exists_emergent ?? 0}</div>
          </div>
          <div className="panel-tight p-4 border border-red-500/30 bg-red-500/5">
            <div className="text-[10px] uppercase tracking-widest text-red-400">Mangler</div>
            <div className="font-fraunces italic text-3xl text-red-300 mt-1">{s.counts?.missing ?? 0}</div>
          </div>
          <div className="panel-tight p-4 border border-amber-500/30 bg-amber-500/5">
            <div className="text-[10px] uppercase tracking-widest text-amber-400">Ingen path / feil</div>
            <div className="font-fraunces italic text-3xl text-amber-300 mt-1">{(s.counts?.no_storage_path || 0) + (s.counts?.errors || 0)}</div>
          </div>
        </div>

        {/* Kontroller */}
        <div className="flex flex-wrap gap-2 mb-4 items-center">
          <label className="flex items-center gap-2 text-xs text-[var(--cream-soft)]">
            <input
              type="checkbox"
              checked={onlyBroken}
              onChange={(e) => { setOnlyBroken(e.target.checked); setOffset(0); }}
              data-testid="filter-only-broken"
            />
            Vis kun ødelagte
          </label>
          <label className="flex items-center gap-2 text-xs text-[var(--cream-soft)] ml-4">
            Antall per side:
            <select
              value={limit}
              onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}
              className="input-editorial text-xs py-1"
              data-testid="filter-limit"
            >
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </label>
          <button
            onClick={load}
            className="btn-ghost text-xs ml-auto inline-flex items-center gap-1"
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Oppdater
          </button>
        </div>

        {/* Tabell */}
        <div className="panel-tight overflow-hidden">
          {err && <div className="p-4 text-red-400 text-sm border-b border-red-400/20 bg-red-500/5">{err}</div>}
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left uppercase tracking-widest text-[var(--cream-soft)] border-b border-white/10 text-[10px]">
                <th className="p-2">R2</th>
                <th className="p-2">Type</th>
                <th className="p-2">Bruker</th>
                <th className="p-2">Storage-path</th>
                <th className="p-2">Thumb</th>
                <th className="p-2">Opprettet</th>
                <th className="p-2">ID</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-8 text-center text-[var(--cream-soft)]">Laster… (kan ta 5-15 sek ved store batcher)</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-emerald-400">Ingen ødelagte bilder funnet. 🎉</td></tr>
              ) : items.map((it) => (
                <tr key={it.id} className={`border-b border-white/5 ${it.r2_status === "MISSING" ? "bg-red-500/[0.03]" : ""}`} data-testid={`media-row-${it.id}`}>
                  <td className="p-2"><StatusBadge status={it.r2_status} /></td>
                  <td className="p-2 text-[var(--cream-soft)]">{it.media_type}</td>
                  <td className="p-2">
                    {it.username ? (
                      <Link to={`/u/${it.username}`} className="hover:text-[var(--magenta)]">@{it.username}</Link>
                    ) : <span className="text-white/30">–</span>}
                  </td>
                  <td className="p-2 font-mono text-[10px] text-[var(--cream-soft)] break-all">{it.storage_path || <span className="text-red-400">null</span>}</td>
                  <td className="p-2">
                    {it.has_thumbnail_url ? <span className="text-emerald-400">✓</span> : <span className="text-white/30">–</span>}
                  </td>
                  <td className="p-2 text-[10px] text-[var(--cream-faint)]">{it.created_at ? new Date(it.created_at).toLocaleString("nb-NO") : "–"}</td>
                  <td className="p-2 font-mono text-[10px] text-white/40">{String(it.id || "").slice(0, 12)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Paginering */}
        {!loading && s.sampled > 0 && (
          <div className="flex items-center justify-between mt-4 text-xs text-[var(--cream-soft)]">
            <div>Viser {s.returned} av {s.sampled} sampled fra {s.total_in_db} totalt</div>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - limit))}
                className="btn-ghost disabled:opacity-40"
                data-testid="page-prev"
              >
                Forrige
              </button>
              <button
                type="button"
                disabled={offset + limit >= s.total_in_db}
                onClick={() => setOffset(offset + limit)}
                className="btn-ghost disabled:opacity-40"
                data-testid="page-next"
              >
                Neste
              </button>
            </div>
          </div>
        )}

        <p className="text-[10px] text-[var(--cream-faint)] mt-6">
          R2-sjekk gjøres via HEAD-request per bilde. Ved MISSING-status betyr det at fila
          er borte fra R2-bucketen selv om DB-oppføringen finnes. Ingen path = feil under opplasting.
        </p>
      </div>
    </Layout>
  );
}
